
///
/// EarthBrowser
///
/// Redux state validation utilities
/// Validates and sanitizes earthBrowser state to prevent corruption
///

import { Constants } from "@/constants"
import { cDefaultSatelliteSets, cSatelliteSets } from "@/earthbrowser/dataset-distribution/satellites/satelliteSets"
import { wgs } from "@/earthbrowser/utils/geographic"
import type { CameraSavedState } from "@/types/CameraSavedState"
import type { DatasetType } from "@/types/DatasetElement"
import type { BuoyRequestParams } from "@shared-types/buoy"
import type { EarthquakeRequestParams, WildfireRequestParams } from "@shared-types/data"
import type { LocationInfo } from "@shared-types/location-info"
import type { NexradAllStationsParams } from "@shared-types/nexrad"
import type {
	EarthBrowserState,
	VolcanoViewMode,
	GeoPackageLayerVisibility,
	ExternalGeoPackageRef,
	TimeZoneMode,
	TourSettings,
} from "../earthBrowserSlice"

// Defined here (not in the slice) so this module stays a value-import leaf:
// the slice already value-imports validators from this file
export const cDefaultTourSettings: TourSettings = {
	autoOrbit: false,
	idleDelaySeconds: 45,
	orbitSpeed: 0.02,
}
import { trig } from "@stovokor/math/math"

// Valid dataset types
const VALID_DATASET_TYPES: DatasetType[] = [
	"satellite", "airport", "buoy", "webcam", "city_weather", "earthquake", "volcano",
	"location", "cloud", "precipitation", "radar", "radar_forecast", "cloud_forecast", "sst", "stars", "aurora", "wildfire", "hurricane", "alert", "placemark",
	"weather", "package", "devtools",
	"nexrad", "synoptic", "fronts", "jetstream", "upper_air", "borders", "division_labels", "roads"
]

// Validation helper functions
function isValidNumber(value: unknown, min = -Infinity, max = Infinity): boolean {
	return typeof value === "number" && !isNaN(value) && isFinite(value) && value >= min && value <= max
}

function clampNumber(value: number, min: number, max: number, defaultValue: number): number {
	if (!isValidNumber(value, min, max)) return defaultValue
	return Math.max(min, Math.min(max, value))
}

// Camera state validation
export function validateCameraState(state: Partial<CameraSavedState>): CameraSavedState {
	const defaults: CameraSavedState = {
		radius: wgs.a + 30000,
		theta: 0,
		phi: trig.half_pi,
		tilt: 0,
		bearing: 0
	}

	// Validate radius — clamp to valid range rather than resetting to default
	const minRadius = Constants.cMinCameraAltitude + wgs.a
	const maxRadius = Constants.cMaxCameraAltitude + wgs.a
	const radius = (typeof state.radius === "number" && isFinite(state.radius))
		? Math.max(minRadius, Math.min(maxRadius, state.radius))
		: defaults.radius

	// Validate angles (allow any finite number, will be normalized by app)
	const theta = isValidNumber(state.theta) ? state.theta! : defaults.theta
	const phi = isValidNumber(state.phi) ? state.phi! : defaults.phi
	const tilt = isValidNumber(state.tilt) ? state.tilt! : defaults.tilt
	const bearing = isValidNumber(state.bearing) ? state.bearing! : defaults.bearing

	const validated: CameraSavedState = { radius, theta, phi, tilt, bearing }

	// Only log in development and if values actually changed
	if (process.env.NODE_ENV !== "production" && JSON.stringify(validated) !== JSON.stringify(state)) {
		const issues: string[] = []
		if (state.radius !== radius) issues.push(`radius: ${state.radius} → ${radius}`)
		if (state.theta !== theta) issues.push(`theta: ${state.theta} → ${theta}`)
		if (state.phi !== phi) issues.push(`phi: ${state.phi} → ${phi}`)
		if (state.tilt !== tilt) issues.push(`tilt: ${state.tilt} → ${tilt}`)
		if (state.bearing !== bearing) issues.push(`bearing: ${state.bearing} → ${bearing}`)

		if (issues.length > 0) {
			console.warn("[State Validation] Fixed invalid camera state:", issues.join(", "))
		}
	}

	return validated
}

// Location info validation
export function validateLocationInfo(loc: Partial<LocationInfo>): LocationInfo {
	const defaults: LocationInfo = {
		geoid: 0,
		name: "",
		country: "",
		admin1: "",
		admin2: "",
		tz: "",
		pop: 0,
		lat: 0,
		lon: 0,
		alt: 0,
		code: ""
	}

	return {
		geoid: isValidNumber(loc.geoid, 0) ? loc.geoid! : defaults.geoid,
		name: typeof loc.name === "string" ? loc.name : defaults.name,
		country: typeof loc.country === "string" ? loc.country : defaults.country,
		admin1: typeof loc.admin1 === "string" ? loc.admin1 : defaults.admin1,
		admin2: typeof loc.admin2 === "string" ? loc.admin2 : defaults.admin2,
		tz: typeof loc.tz === "string" ? loc.tz : defaults.tz,
		pop: isValidNumber(loc.pop, 0) ? loc.pop! : defaults.pop,
		lat: isValidNumber(loc.lat, -90, 90) ? loc.lat! : defaults.lat,
		lon: isValidNumber(loc.lon, -180, 180) ? loc.lon! : defaults.lon,
		alt: isValidNumber(loc.alt) ? loc.alt! : defaults.alt,
		code: typeof loc.code === "string" ? loc.code : defaults.code
	}
}

// Earthquake search params validation
export function validateEarthquakeSearchParams(params: Partial<EarthquakeRequestParams>): EarthquakeRequestParams {
	return {
		count: clampNumber(params.count || 400, 1, 10000, 400),
		age: clampNumber(params.age || 30 * 86400, 0, 365 * 86400, 30 * 86400)
	}
}

// Wildfire request params validation
export function validateWildfireRequestParams(params: Partial<WildfireRequestParams>): WildfireRequestParams {
	return {
		count: clampNumber(params.count || 100, 1, 10000, 100),
		age: clampNumber(params.age || 30 * 86400, 0, 365 * 86400, 30 * 86400)
	}
}

// Satellite sets: known set ids only, de-duplicated, in catalog order. An
// empty selection is legal (the layer shows nothing but pinned satellites)
export function validateSatelliteSets(sets: unknown): string[] {
	if (!Array.isArray(sets)) return [...cDefaultSatelliteSets]
	return cSatelliteSets.map(s => s.id).filter(id => sets.includes(id))
}

// Buoy search params validation
export function validateBuoySearchParams(params: Partial<BuoyRequestParams>): BuoyRequestParams {
	return {
		count: clampNumber(params.count || 500, 1, 2000, 500),
		dart: typeof params.dart === "boolean" ? params.dart : false
	}
}

// NEXRAD radar search params validation
export function validateNexradSearchParams(params: Partial<NexradAllStationsParams>): NexradAllStationsParams {
	return {
		hours: clampNumber(params.hours || 2, 1, 24, 2),
		count: clampNumber(params.count || 200, 1, 500, 200)
	}
}

// Clock state validation
export function validateClockState(state: unknown): "animating" | "paused" | "real-time" {
	if (state === "animating" || state === "paused" || state === "real-time") {
		return state
	}
	console.warn("[State Validation] Invalid clock state:", state)
	return "real-time"
}

// Time zone display mode validation (defaults to local time)
export function validateTimeZoneMode(mode: unknown): TimeZoneMode {
	if (mode === "local" || mode === "utc") {
		return mode
	}
	return "local"
}

// Volcano view mode validation
// VolcanoViewMode imported from earthBrowserSlice

export function validateVolcanoViewMode(mode: unknown): VolcanoViewMode {
	if (mode === "topographical" || mode === "3d") {
		return mode
	}
	console.warn("[State Validation] Invalid volcano view mode:", mode)
	return "topographical"
}

/** Silent membership test; use where an unknown value is expected and shouldn't warn */
export function isDatasetType(value: unknown): value is DatasetType {
	return typeof value === "string" && VALID_DATASET_TYPES.includes(value as DatasetType)
}

// Dataset type validation
export function validateDatasetType(type: unknown): DatasetType {
	if (typeof type === "string" && VALID_DATASET_TYPES.includes(type as DatasetType)) {
		return type as DatasetType
	}
	console.warn("[State Validation] Invalid dataset type:", type)
	return ""
}

// Dataset array validation (removes duplicates and invalid types)
export function validateDatasetArray(datasets: unknown): DatasetType[] {
	if (!Array.isArray(datasets)) {
		console.warn("[State Validation] Expected array for datasets:", datasets)
		return []
	}

	const validDatasets = datasets
		.filter((item): item is DatasetType => {
			if (typeof item === "string" && VALID_DATASET_TYPES.includes(item as DatasetType)) {
				return true
			}
			console.warn("[State Validation] Filtering out invalid dataset:", item)
			return false
		})

	// Remove duplicates
	const unique = [...new Set(validDatasets)]

	if (unique.length !== datasets.length) {
		console.warn("[State Validation] Removed duplicates or invalid datasets from array")
	}

	return unique
}

// GeoPackage layer visibility validation
// GeoPackageLayerVisibility imported from earthBrowserSlice

export function validateGeoPackageLayerVisibility(visibility: unknown): GeoPackageLayerVisibility {
	if (!visibility || typeof visibility !== "object" || Array.isArray(visibility)) {
		return {}
	}

	const validated: GeoPackageLayerVisibility = {}

	for (const [packageId, layers] of Object.entries(visibility as Record<string, unknown>)) {
		// Validate package ID is a non-empty string
		if (typeof packageId !== "string" || packageId.length === 0) {
			console.warn("[State Validation] Invalid GeoPackage package ID:", packageId)
			continue
		}

		// Validate layers is an object
		if (!layers || typeof layers !== "object" || Array.isArray(layers)) {
			console.warn("[State Validation] Invalid GeoPackage layers for package:", packageId)
			continue
		}

		const validatedLayers: Record<string, boolean> = {}

		for (const [layerName, visible] of Object.entries(layers as Record<string, unknown>)) {
			// Validate layer name is a non-empty string
			if (typeof layerName !== "string" || layerName.length === 0) {
				console.warn("[State Validation] Invalid GeoPackage layer name:", layerName)
				continue
			}

			// Validate visible is a boolean
			if (typeof visible === "boolean") {
				validatedLayers[layerName] = visible
			} else {
				console.warn("[State Validation] Invalid GeoPackage layer visibility value:", visible)
				validatedLayers[layerName] = true // Default to visible
			}
		}

		if (Object.keys(validatedLayers).length > 0) {
			validated[packageId] = validatedLayers
		}
	}

	return validated
}

// GeoPackage dataset visibility validation
export function validateGeoPackageDatasetVisible(visibility: unknown): Record<string, boolean> {
	if (!visibility || typeof visibility !== "object" || Array.isArray(visibility)) {
		return {}
	}

	const validated: Record<string, boolean> = {}

	for (const [packageId, visible] of Object.entries(visibility)) {
		// Validate package ID is a non-empty string
		if (typeof packageId !== "string" || packageId.length === 0) {
			console.warn("[State Validation] Invalid GeoPackage package ID:", packageId)
			continue
		}

		// Validate visible is a boolean
		if (typeof visible === "boolean") {
			validated[packageId] = visible
		} else {
			console.warn("[State Validation] Invalid GeoPackage dataset visibility value:", visible)
			validated[packageId] = true // Default to visible
		}
	}

	return validated
}

// External GeoPackages validation
export function validateExternalGeoPackages(packages: unknown): Record<string, ExternalGeoPackageRef> {
	if (!packages || typeof packages !== "object" || Array.isArray(packages)) {
		return {}
	}

	const validated: Record<string, ExternalGeoPackageRef> = {}

	for (const [packageId, pkg] of Object.entries(packages)) {
		// Validate package ID is a non-empty string
		if (typeof packageId !== "string" || packageId.length === 0) {
			console.warn("[State Validation] Invalid external GeoPackage ID:", packageId)
			continue
		}

		// Validate package object structure
		if (!pkg || typeof pkg !== "object" || Array.isArray(pkg)) {
			console.warn("[State Validation] Invalid external GeoPackage object:", packageId)
			continue
		}

		const pkgObj = pkg as Record<string, unknown>

		// Validate required fields
		if (typeof pkgObj.id !== "string" || pkgObj.id.length === 0) {
			console.warn("[State Validation] Invalid external GeoPackage id field:", packageId)
			continue
		}

		if (typeof pkgObj.name !== "string" || pkgObj.name.length === 0) {
			console.warn("[State Validation] Invalid external GeoPackage name field:", packageId)
			continue
		}

		validated[packageId] = {
			id: pkgObj.id,
			name: pkgObj.name,
			loaded: typeof pkgObj.loaded === "boolean" ? pkgObj.loaded : false,
			visible: typeof pkgObj.visible === "boolean" ? pkgObj.visible : true
		}
	}

	return validated
}

// EarthBrowserState imported from earthBrowserSlice

/**
 * Check if earthBrowser state is valid
 * Returns true if all fields are valid, false if any corruption is detected
 */
export function isStateValid(state: Partial<EarthBrowserState>): boolean {
	try {
		// Check camera state for null/undefined values
		if (!state.cameraState || typeof state.cameraState !== "object") return false
		const cs = state.cameraState
		if (!isValidNumber(cs.radius, Constants.cMinCameraAltitude + wgs.a, Constants.cMaxCameraAltitude + wgs.a)) return false
		if (!isValidNumber(cs.theta)) return false
		if (!isValidNumber(cs.phi)) return false
		if (!isValidNumber(cs.tilt)) return false
		if (!isValidNumber(cs.bearing)) return false

		// Allow undefined for boolean fields (they can be defaulted)
		if (state.timeControlExpanded !== undefined && typeof state.timeControlExpanded !== "boolean") return false

		// Allow undefined for clock state (can be defaulted)
		if (state.clockState !== undefined) {
			// Check clock state is valid enum if it exists
			if (state.clockState !== "animating" && state.clockState !== "paused" && state.clockState !== "real-time") return false
		}

		// Allow undefined for numeric fields (they can be defaulted), but validate if present
		// Speed range covers 0.1x up to one day per second (86400x) for weather animation
		if (state.clockAnimatingSpeed !== undefined && !isValidNumber(state.clockAnimatingSpeed, 0.1, 86400)) return false
		if (state.clockCurrentTime !== undefined && !isValidNumber(state.clockCurrentTime, 0)) return false
		if (state.timeZoneMode !== undefined && state.timeZoneMode !== "local" && state.timeZoneMode !== "utc") return false

		// Check arrays are actually arrays (if present)
		if (state.visibleDatasets !== undefined && !Array.isArray(state.visibleDatasets)) return false
		if (state.loadingDatasets !== undefined && !Array.isArray(state.loadingDatasets)) return false

		// Allow undefined for volcanoViewMode (can be defaulted)
		if (state.volcanoViewMode !== undefined) {
			if (state.volcanoViewMode !== "topographical" && state.volcanoViewMode !== "3d") return false
		}

		// Check GeoPackage visibility structures are objects (if present)
		if (state.geopackageLayerVisibility !== undefined) {
			if (typeof state.geopackageLayerVisibility !== "object" || Array.isArray(state.geopackageLayerVisibility)) {
				return false
			}
		}
		if (state.geopackageDatasetVisible !== undefined) {
			if (typeof state.geopackageDatasetVisible !== "object" || Array.isArray(state.geopackageDatasetVisible)) {
				return false
			}
		}

		return true
	} catch {
		return false
	}
}

export function validateEarthBrowserState(state: Partial<EarthBrowserState>): EarthBrowserState {
	const validated: EarthBrowserState = {
		cameraState: validateCameraState(state.cameraState || {}),
		cameraLocation: validateLocationInfo(state.cameraLocation || {}),
		earthquakeSearchParams: validateEarthquakeSearchParams(state.earthquakeSearchParams || {}),
		wildfireRequestParams: validateWildfireRequestParams(state.wildfireRequestParams || {}),
		satelliteSets: validateSatelliteSets(state.satelliteSets),
		buoySearchParams: validateBuoySearchParams(state.buoySearchParams || {}),
		nexradSearchParams: validateNexradSearchParams(state.nexradSearchParams || {}),
		clockState: validateClockState(state.clockState),
		clockAnimatingSpeed: clampNumber(state.clockAnimatingSpeed || 600, 0.1, 86400, 600),
		clockCurrentTime: isValidNumber(state.clockCurrentTime, 0)
			? state.clockCurrentTime!
			: Date.now(),
		clockStartOffset: clampNumber(state.clockStartOffset ?? 48, 0, 365, 48),
		clockEndOffset: clampNumber(state.clockEndOffset ?? 12, 0, 365, 12),
		timeZoneMode: validateTimeZoneMode(state.timeZoneMode),
		visibleDatasets: validateDatasetArray(state.visibleDatasets),
		loadingDatasets: validateDatasetArray(state.loadingDatasets),
		sideTableContent: state.sideTableContent === null || validateDatasetType(state.sideTableContent) !== ""
			? (state.sideTableContent as DatasetType | null)
			: null,
		sideTableSize: clampNumber(state.sideTableSize || 20, 1, 100, 20),
		timeControlExpanded: typeof state.timeControlExpanded === "boolean"
			? state.timeControlExpanded
			: false,
		volcanoViewMode: validateVolcanoViewMode(state.volcanoViewMode),
		municipalityBordersVisible: typeof state.municipalityBordersVisible === "boolean"
			? state.municipalityBordersVisible
			: false,
		geopackageLayerVisibility: validateGeoPackageLayerVisibility(state.geopackageLayerVisibility),
		geopackageDatasetVisible: validateGeoPackageDatasetVisible(state.geopackageDatasetVisible),
		externalGeoPackages: validateExternalGeoPackages(state.externalGeoPackages),
		geopackageDownloadDirectoryId: validateGeoPackageDownloadDirectoryId(state.geopackageDownloadDirectoryId),
		localDataDirectoryId: validateLocalDataDirectoryId(state.localDataDirectoryId),
		tourSettings: validateTourSettings(state.tourSettings),
	}

	return validated
}

/**
 * Validate automated camera motion settings (CameraDirector orbit)
 */
export function validateTourSettings(settings: unknown): TourSettings {
	const s = settings as Partial<TourSettings> | null | undefined
	return {
		autoOrbit: typeof s?.autoOrbit === "boolean" ? s.autoOrbit : cDefaultTourSettings.autoOrbit,
		idleDelaySeconds: clampNumber(s?.idleDelaySeconds ?? cDefaultTourSettings.idleDelaySeconds, 10, 600, cDefaultTourSettings.idleDelaySeconds),
		orbitSpeed: clampNumber(s?.orbitSpeed ?? cDefaultTourSettings.orbitSpeed, 0.002, 0.2, cDefaultTourSettings.orbitSpeed),
	}
}

// GeoPackage download directory validation
export function validateGeoPackageDownloadDirectoryId(directoryId: unknown): string | null {
	// Must be null or a non-empty string
	if (directoryId === null) {
		return null
	}

	if (typeof directoryId === 'string' && directoryId.length > 0) {
		return directoryId
	}

	// Invalid value - reset to null
	if (process.env.NODE_ENV !== 'production') {
		console.warn('[State Validation] Invalid geopackageDownloadDirectoryId, resetting to null:', directoryId)
	}
	return null
}

// Local data directory validation
export function validateLocalDataDirectoryId(directoryId: unknown): string | null {
	if (directoryId === null) {
		return null
	}

	if (typeof directoryId === 'string' && directoryId.length > 0) {
		return directoryId
	}

	if (process.env.NODE_ENV !== 'production') {
		console.warn('[State Validation] Invalid localDataDirectoryId, resetting to null:', directoryId)
	}
	return null
}
